import React from 'react';
import { Box, AppBar, Toolbar, Typography, IconButton, Select, MenuItem, Chip, Button } from '@mui/material';
import { ArrowBack, Language as AllSitesIcon, DirectionsBus, CampaignOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { COMPANY_NAME, APP_NAME_FULL } from '../constants';
import { useAuth } from '../contexts/AuthContext';

const AppHeader = ({ backTo, rightContent }) => {
  const navigate = useNavigate();
  const { visibleSites = [], currentSite, setCurrentSite, isSuperAdmin, canSwitchSites, user } = useAuth();
  // Only show Transportation in nav for role exactly "transportation" (others use Dashboard card)
  const showTransportationLink = user && user.role === 'transportation';
  const showCommunicationsLink =
    user && ['superadmin', 'admin', 'editor', 'viewer'].includes(user.role);
  const showBack = backTo != null && backTo !== '';

  const handleSiteChange = (e) => {
    const val = e.target.value;
    if (val === '') {
      setCurrentSite(null);
    } else {
      const site = visibleSites.find(s => s.id === Number(val));
      if (site) setCurrentSite(site);
    }
  };

  const siteDisplay = currentSite ? currentSite.name : 'All Sites';

  const renderSitePicker = () => {
    if (canSwitchSites) {
      return (
        <Select
          value={currentSite?.id ?? ''}
          onChange={handleSiteChange}
          size="small"
          variant="outlined"
          displayEmpty
          sx={{
            color: '#fff',
            minWidth: 150,
            '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.4)' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.7)' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#fff' },
            '.MuiSvgIcon-root': { color: '#fff' },
            '.MuiSelect-select': { py: 0.75 },
          }}
          renderValue={() => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {!currentSite && <AllSitesIcon sx={{ fontSize: 18, opacity: 0.9 }} />}
              <Typography variant="body2" sx={{ fontWeight: 600, color: !currentSite ? '#bbdefb' : '#fff' }}>
                {siteDisplay}
              </Typography>
            </Box>
          )}
        >
          {isSuperAdmin && (
            <MenuItem value="">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AllSitesIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography>All Sites</Typography>
              </Box>
            </MenuItem>
          )}
          {visibleSites.map(s => (
            <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
          ))}
        </Select>
      );
    }

    return (
      <Chip
        label={siteDisplay}
        size="small"
        sx={{
          bgcolor: 'rgba(255,255,255,0.2)',
          color: '#fff',
          fontWeight: 600,
          '& .MuiChip-label': { px: 1.5 },
        }}
      />
    );
  };

  return (
    <Box sx={{ pt: 1.5, px: 1.5 }}>
      <AppBar
        position="static"
        sx={{
          backgroundColor: 'transparent',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          borderRadius: '16px',
          overflow: 'hidden',
        }}
      >
        <Toolbar sx={{ px: 0, minHeight: { xs: 64, sm: 72 }, width: '100%' }}>
          {showBack && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#fff',
                borderTopLeftRadius: 16,
                borderBottomLeftRadius: 16,
                width: 48,
                minWidth: 48,
                alignSelf: 'stretch',
              }}
            >
              <IconButton
                onClick={() => navigate(backTo)}
                sx={{ color: '#1a1a1a' }}
                aria-label="Back"
              >
                <ArrowBack />
              </IconButton>
            </Box>
          )}
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                background: 'linear-gradient(to right, #ffffff 0%, #e3f2fd 35%, #90caf9 70%, #1976d2 100%)',
                pr: 3,
                py: 0.5,
                pl: 2,
                borderTopLeftRadius: showBack ? 0 : 16,
                borderBottomLeftRadius: showBack ? 0 : 16,
              }}
            >
              <img
                src={process.env.PUBLIC_URL + '/logo.png'}
                alt=""
                style={{
                  height: 48,
                  width: 'auto',
                  maxWidth: 120,
                  objectFit: 'contain',
                  display: 'block',
                }}
                onError={(e) => {
                  const el = e.target;
                  if (!el.dataset.triedSvg) {
                    el.dataset.triedSvg = '1';
                    el.src = process.env.PUBLIC_URL + '/logo.svg';
                  }
                }}
              />
              <Box>
                <Typography variant="subtitle1" component="div" sx={{ fontWeight: 600, lineHeight: 1.2, color: '#1a1a1a' }}>
                  {COMPANY_NAME}
                </Typography>
                <Typography variant="subtitle1" component="div" sx={{ opacity: 0.9, color: '#1a1a1a' }}>
                  {APP_NAME_FULL}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                minWidth: 0,
                bgcolor: '#1976d2',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 0,
                alignSelf: 'stretch',
                borderTopRightRadius: 16,
                borderBottomRightRadius: 16,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0, gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  {renderSitePicker()}
                </Box>
                {showTransportationLink && (
                  <Button
                    size="small"
                    startIcon={<DirectionsBus />}
                    onClick={() => navigate('/transportation')}
                    sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                  >
                    Transportation
                  </Button>
                )}
                {showCommunicationsLink && (
                  <Button
                    size="small"
                    startIcon={<CampaignOutlined />}
                    onClick={() => navigate('/communications')}
                    sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                  >
                    Communications
                  </Button>
                )}
                <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                  {rightContent}
                </Box>
              </Box>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
    </Box>
  );
};

export default AppHeader;
